import {createContext, ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {SiteConfigApi} from "../api/SiteConfigApi";
import {MAINTENANCE_EVENT} from "../api/http";

interface SiteStatus {
    // Whether the site is down for maintenance: only administrators can use it (SiteLayout shows everyone else the
    // maintenance message). False until the API says otherwise, so the site doesn't flash the message when it's off.
    maintenance: boolean;
    // Whether the API has answered (or couldn't be asked): until then, maintenance is only a guess.
    checked: boolean;
    // After the switch is flipped (Administration > Settings).
    setMaintenance(maintenance: boolean): void;
}

const SiteStatusContext = createContext<SiteStatus | null>(null);

// Asks the API once per page load whether the site is down for maintenance, and listens for its maintenance answer to
// any call (src/api/http.ts), so someone already on the site when it's turned on sees the message at their next click.
export function SiteStatusProvider({children}: { children: ReactNode }) {
    const [maintenance, setMaintenance] = useState(false);
    const [checked, setChecked] = useState(false);
    useEffect(() => {
        let current = true;
        SiteConfigApi.get().then(
            config => {
                if (current) {
                    setMaintenance(config.maintenanceMode === true);
                    setChecked(true);
                }
            },
            () => { if (current) setChecked(true); });
        const onMaintenance = () => setMaintenance(true);
        window.addEventListener(MAINTENANCE_EVENT, onMaintenance);
        return () => {
            current = false;
            window.removeEventListener(MAINTENANCE_EVENT, onMaintenance);
        };
    }, []);
    const state = useMemo<SiteStatus>(() => ({ maintenance, checked, setMaintenance }), [maintenance, checked]);
    return <SiteStatusContext.Provider value={state}>{children}</SiteStatusContext.Provider>;
}

export function useSiteStatus(): SiteStatus {
    const state = useContext(SiteStatusContext);
    if (!state) {
        throw new Error('useSiteStatus needs a SiteStatusProvider');
    }
    return state;
}
